import { inject, Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import {
  AnalyticsApiService,
  CompositeFilterOperator,
  FieldFilterOperator,
  Filter,
  MarketId,
  MeasureAggregateOperator,
  QueryMetricsRequest,
  QueryMetricsResponse,
  ResourceId,
} from '@vendasta/multi-location-analytics';
import { DateRange, GetPartnerLeadStatsRequest, PartnerApiService } from '@vendasta/partner';

const marketResource = (marketId: string) => [new ResourceId({ marketId: new MarketId({ marketIds: [marketId] }) })];

function buildBaseClientLeadsTotalRequest(partnerId: string, marketId = '__ALL__'): QueryMetricsRequest {
  const queryFilters = [
    'AI Voice Receptionist',
    'Email',
    'Facebook Messenger',
    'Form',
    'Inbox',
    'Instagram Messaging',
    'SMS',
    'Web Chat',
    'WhatsApp',
  ];
  const filters = [];
  queryFilters.forEach((filter) => {
    filters.push({
      fieldFilter: {
        dimension: 'source_name',
        operator: FieldFilterOperator.EQUAL,
        value: {
          value: filter,
        },
      },
    });
  });
  const clientLeadsSourceFilter = new Filter({
    compositeFilter: {
      op: CompositeFilterOperator.OR,
      filters: filters,
    },
  });
  return new QueryMetricsRequest({
    partnerId: partnerId,
    metricName: 'contact_source_market',
    resourceIds: marketResource(marketId),
    measures: [
      {
        aggregate: {
          measure: 'contact_id',
          aggOp: MeasureAggregateOperator.COUNT,
          filter: clientLeadsSourceFilter,
        },
      },
    ],
  });
}

function buildClientLeadsTotalRequest(partnerId: string, marketId = '__ALL__'): QueryMetricsRequest {
  return buildBaseClientLeadsTotalRequest(partnerId, marketId);
}

function buildClientLeadsLast30DaysRequest(partnerId: string, marketId = '__ALL__'): QueryMetricsRequest {
  const dateRange = getLast30Days();
  const baseRequest = buildBaseClientLeadsTotalRequest(partnerId, marketId);
  return new QueryMetricsRequest({
    ...baseRequest,
    dateRange: {
      start: dateRange.start,
      end: dateRange.end,
    },
  });
}

function fromQueryMetricsResponse(
  total: QueryMetricsResponse,
  last30Days: QueryMetricsResponse,
): { total: number; last30Days: number } {
  return {
    total: getValueFromQueryMetricsResponse(total),
    last30Days: getValueFromQueryMetricsResponse(last30Days),
  };
}

function getValueFromQueryMetricsResponse(response: QueryMetricsResponse): number {
  if ((response?.metricResults || []).length === 0) return 0;
  if ((response?.metricResults[0]?.metrics?.metrics || []).length === 0) return 0;
  if ((response?.metricResults[0]?.metrics?.metrics[0]?.measures || []).length === 0) return 0;
  return response.metricResults[0].metrics.metrics[0].measures[0];
}

function getLast30Days(): { start: Date; end: Date } {
  const now = new Date();

  // Get yesterday's midnight in local time
  const endLocal = new Date(now);
  endLocal.setHours(23, 59, 59, 999);
  endLocal.setDate(endLocal.getDate() - 1);

  // Get 30 days before yesterday's midnight in local time
  const startLocal = new Date(endLocal);
  startLocal.setDate(startLocal.getDate() - 29); // Since endLocal is already -1 day
  startLocal.setHours(0, 0, 0, 0);

  // Convert both to UTC
  const start = new Date(startLocal.toISOString());
  const end = new Date(endLocal.toISOString());

  return { start: start, end: end };
}

@Injectable({
  providedIn: 'root',
})
export class LeadMetricsService {
  private readonly analyticsService = inject(AnalyticsApiService);
  private readonly partnerService = inject(PartnerApiService);

  async getClientLeads(partnerId: string, marketId: string): Promise<{ total: number; last30Days: number }> {
    const [total, last30Days] = await Promise.all([
      firstValueFrom(this.analyticsService.queryMetrics(buildClientLeadsTotalRequest(partnerId, marketId))),
      firstValueFrom(this.analyticsService.queryMetrics(buildClientLeadsLast30DaysRequest(partnerId, marketId))),
    ]);
    return fromQueryMetricsResponse(total, last30Days);
  }

  async getPartnerLeads(partnerId: string, marketId: string): Promise<{ total: number; last30Days: number }> {
    const allTimeReq = new GetPartnerLeadStatsRequest({ partnerId: partnerId, marketId: marketId });
    const last30DaysReq = new GetPartnerLeadStatsRequest({
      partnerId: partnerId,
      marketId: marketId,
      dateRange: new DateRange(getLast30Days()),
    });
    const [total, last30Days] = await Promise.all([
      firstValueFrom(this.partnerService.getPartnerLeadStats(allTimeReq).pipe(map((res) => res?.leadsCount || 0))),
      firstValueFrom(this.partnerService.getPartnerLeadStats(last30DaysReq).pipe(map((res) => res?.leadsCount || 0))),
    ]);
    return { total: total, last30Days: last30Days };
  }
}
